import { useEffect, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import noop from 'lodash/noop';
import {
  useFacilityMenuGet,
  useFacilityMenuItems,
} from 'shared-feature-facility-menu';

import {
  useAccountGet,
  useApi,
  useWebPunchSession,
} from '@sbv-clients/data-access-api';
import { useAuth } from '@sbv-clients/shared-feature-auth';
import {
  Navbar,
  Sidebar,
  useBreadcrumbLabelsBuilder,
} from '@sbv-clients/shared-feature-layouts';
import {
  Box,
  CssBaseline,
  GlobalStyles,
  styled,
  usePreferenceContext,
  useToggleModal,
} from '@sbv-clients/ui-shared';
import { addDays, formatDate } from '@sbv-clients/util-misc';

import { useMixpanelIdentity } from '../tracking/use-mixpanel-identity';
import NoAccessState from './no-access-state';

const Main = styled(Box)(({ theme }) => ({
  marginLeft: 8,
  transition: 'margin-left 0.3s ease-in-out',
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  maxWidth: '100%',
  overflow: 'hidden',
  backgroundColor: theme.background?.white,
  border: `1px solid ${theme.border?.profileTitle}`,
  boxShadow: theme.colors.shadows.layout,
  borderRadius: '20px',
}));

const Drawer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'drawerWidth',
})<{ drawerWidth: number }>(({ theme, drawerWidth }) => ({
  transition: 'width 0.3s ease-in-out',
  [theme.breakpoints.up('md')]: {
    width: drawerWidth,
    flexShrink: 0,
  },
}));

interface PageContentProps {
  showNavBar: boolean;
  onToggleDrawer: () => void;
  toggleSidebarMinimized: () => void;
  isSidebarMinimized: boolean;
}

const PageContent = ({
  showNavBar,
  onToggleDrawer,
  toggleSidebarMinimized,
  isSidebarMinimized,
}: PageContentProps) => {
  const { data: facilityMenuItems = [] } = useFacilityMenuGet();
  const { breadCrumbLabels } = useBreadcrumbLabelsBuilder(facilityMenuItems);
  const { pathname } = useLocation();
  const { data } = useAccountGet();
  const { isMobileSession } = useWebPunchSession();
  const { allMenuItems, isSuccess: isMenuLoaded } = useFacilityMenuItems();
  const hasNoTabs = isMenuLoaded && !allMenuItems?.length;

  return (
    <Main m={2} borderRadius={1}>
      {showNavBar && (
        <Navbar
          showContactUs
          showRemoteSupport={!isMobileSession}
          breadCrumbLabels={breadCrumbLabels}
          onToggleDrawer={onToggleDrawer}
          minimisedViewAllowed
          isSidebarMinimized={isSidebarMinimized}
          toggleSidebarMinimized={toggleSidebarMinimized}
          showPreferences={!isMobileSession}
          showProfiles
          showResetConfiguration={!isMobileSession}
          showMobileEmployeesButton={pathname?.includes('/employees')}
          profileImage={data?.profileImage ?? ''}
        />
      )}
      <Box flex={1} overflow={{ xs: 'auto', sm: 'hidden' }}>
        {hasNoTabs ? <NoAccessState /> : <Outlet />}
      </Box>
    </Main>
  );
};

const PrivateRouteLayout = () => {
  const {
    showSideBar,
    showNavBar,
    isSidebarMinimized,
    toggleSidebarMinimized,
    setSidebarMinimized,
  } = usePreferenceContext();
  const { open, closeModal, toggleModal } = useToggleModal();
  const { pathname } = useLocation();
  useEffect(() => {
    closeModal();
  }, [pathname, closeModal]);
  const drawerWidth = useMemo(() => {
    if (!showSideBar) {
      return 0;
    }
    return isSidebarMinimized ? 60 : 258;
  }, [isSidebarMinimized, showSideBar]);

  useMixpanelIdentity();

  const api = useApi();
  const { isMobileSession } = useWebPunchSession();
  const { data: accountData } = useAccountGet();
  const isSupportSession = accountData?.isSupportSession ?? false;
  useEffect(() => {
    //we are caching the census so that's why we are making blind api call
    if (isMobileSession || isSupportSession) {
      return;
    }
    api.complianceApi
      .complianceCensusGet({
        startDate: formatDate(addDays(new Date(), -30)),
        endDate: formatDate(new Date()),
      })
      .catch(noop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobileSession, isSupportSession]);

  const { parentMenuWithSubMenuItemsType, isLoading } = useFacilityMenuItems();
  return (
    <Box display="flex" height="100%" overflow="hidden">
      <CssBaseline />
      <GlobalStyles />
      {showSideBar && (
        <Drawer drawerWidth={drawerWidth}>
          <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
            <Sidebar
              open={open}
              variant="temporary"
              PaperProps={{
                style: {
                  width: 258,
                  transition: 'width 0.3s ease-in-out',
                },
              }}
              items={parentMenuWithSubMenuItemsType}
              itemsLoading={isLoading}
              minimisedViewAllowed
              onClose={() => {
                closeModal();
                setSidebarMinimized(true);
              }}
            />
          </Box>
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <Sidebar
              open
              variant="persistent"
              PaperProps={{
                style: {
                  width: drawerWidth,
                  transition: 'width 0.3s ease-in-out',
                },
              }}
              items={parentMenuWithSubMenuItemsType}
              isMinimisedView={isSidebarMinimized}
              itemsLoading={isLoading}
              minimisedViewAllowed
              onClose={() => {
                closeModal();
                setSidebarMinimized(true);
              }}
            />
          </Box>
        </Drawer>
      )}
      <PageContent
        onToggleDrawer={toggleModal}
        showNavBar={showNavBar}
        toggleSidebarMinimized={toggleSidebarMinimized}
        isSidebarMinimized={isSidebarMinimized}
      />
    </Box>
  );
};

export default PrivateRouteLayout;
